import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { AlertTriangle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { cleanupProductKnowledgeBase } from '@/shared/services/knowledgeBase';
import { useWorkspace } from '@/shared/hooks/api/useWorkspace';
import { useCleanupMutation } from '@/shared/hooks/useCleanupMutation';
import { CleanupConfirmDialog } from '@/shared/components/cleanup/CleanupConfirmDialog';
import { CleanupProgressDialog } from '@/shared/components/cleanup/CleanupProgressDialog';

const TRANSLATION_PREFIX = 'knowledge.security';

export function KnowledgeSecurity() {
  const { t } = useTranslation();
  const { currentWorkspace: currentProduct } = useWorkspace();
  const [confirmProductName, setConfirmProductName] = useState('');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const { isDeleting, deleteProgress, runCleanup } = useCleanupMutation({
    mutationFn: async () => {
      if (!currentProduct?.id) throw new Error('Product ID not found');
      await cleanupProductKnowledgeBase(currentProduct.id);
    },
    queryKeys: [
      ['knowledge-base', currentProduct?.id],
      ['knowledge-storage', currentProduct?.id],
      ['embeddings', currentProduct?.id],
    ],
    translationPrefix: TRANSLATION_PREFIX,
  });

  const handleCleanup = async () => {
    if (!currentProduct?.name) {
      toast.error(t(`${TRANSLATION_PREFIX}.cleanupErrorInvalid`));
      return;
    }
    if (confirmProductName !== currentProduct.name) {
      toast.error(t(`${TRANSLATION_PREFIX}.cleanupErrorNameMismatch`));
      return;
    }
    setIsDeleteDialogOpen(false);
    setConfirmProductName('');
    await runCleanup();
  };

  return (
    <>
      <Card className="border-destructive/20">
        <CardHeader>
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-full bg-destructive/10">
              <AlertTriangle className="h-5 w-5 text-destructive" />
            </div>
            <div>
              <CardTitle className="text-destructive">{t(`${TRANSLATION_PREFIX}.title`)}</CardTitle>
              <CardDescription>{t(`${TRANSLATION_PREFIX}.description`)}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="border-t border-destructive/20 pt-6">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="font-medium text-foreground">{t(`${TRANSLATION_PREFIX}.cleanupTitle`)}</h3>
                <p className="text-sm text-muted-foreground mt-1">{t(`${TRANSLATION_PREFIX}.cleanupDescription`)}</p>
              </div>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setIsDeleteDialogOpen(true)}
                className="shrink-0"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {t(`${TRANSLATION_PREFIX}.cleanupButton`)}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <CleanupConfirmDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        confirmName={confirmProductName}
        onConfirmNameChange={setConfirmProductName}
        expectedName={currentProduct?.name}
        isDeleting={isDeleting}
        onConfirm={handleCleanup}
        translationPrefix={TRANSLATION_PREFIX}
      />

      <CleanupProgressDialog
        open={isDeleting}
        onOpenChange={() => {}}
        progress={deleteProgress}
        entityName={currentProduct?.name}
        translationPrefix={TRANSLATION_PREFIX}
      />
    </>
  );
}
